import React, { useCallback } from 'react';

import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import some from 'lodash/some';

import useModelSearch from '../../models/ModelSearchContext';
import usePipelineCreationContext from '../PipelineCreationContext';

import Button from '../../common/Button/Button';
import CardView from '../../common/CardView/CardView';
import Label from '../../common/Labels/Label';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';

import ModelSingleSelectionView from '../../models/ModelSingleSelectionView/ModelSingleSelectionView';

const groupModelsById = (models = []) => {
  const modelGroupings = groupBy(models, ({ modelID }) => modelID);
  const modelKeys = Object.keys(modelGroupings);
  return modelKeys.map((modelID) => {
    const latestModelVersion = modelGroupings[modelID][0]; // TODO - Assumes the list is presorted - check if this is valid
    return ({
      modelID,
      latestModelVersion,
      sha: latestModelVersion.sha,
      modelVersion: latestModelVersion.modelVersion,
      createdAt: latestModelVersion?.createdAt,
      updatedAt: latestModelVersion?.updatedAt,
    })
  });
}

export const PipelineAddStepFrame = ({
  models = [],
  pipelineSteps = [],
  originalSteps = [],
  loading,
  error,
  cardTitle,
  handleNext, 
  addStep, 
  removeStep, 
  onClose, 
}) => {
  const isModelSelected = useCallback(modelID => some(pipelineSteps, item => get(item, 'ModelInference.models[0].name') === modelID), [pipelineSteps])
  const isModelInOriginalSteps = useCallback(modelID => some(originalSteps, item => get(item, 'ModelInference.models[0].name') === modelID), [originalSteps])

  const onSelect = useCallback((selectedModelId) => {
    if (isModelSelected(selectedModelId)) {
      removeStep(pipelineSteps.findIndex(stepObj => stepObj?.ModelInference?.models[0]?.name === selectedModelId));
    } else {
      addStep(models?.find(({ modelID }) => modelID === selectedModelId));
    }
  }, [addStep, isModelSelected, models, pipelineSteps, removeStep]);

  return (
    <CardView className="flex flex-col">
      <div className="flex justify-between items-center mb-2">
        <Label bold>{cardTitle}</Label>
        <Button
          variant="close"
          iconSize={24}
          textColor="border-dark"
          hoverTextColor="border-darker"
          activeTextColor="info"
          onClick={onClose}
        />
      </div>
      <Label className="mb-2 underline" bold>Add More Pipeline Steps</Label>
      <div className="flex border rounded overflow-scroll flex-grow relative">
        {
          loading ? (
            <LoadingSpinner
              animationKey="step-selection-loader"
              loadingText="Loading Models..."
              useOverlay
            />
          ) : (
            <ModelSingleSelectionView
              models={models}
              error={error}
              isModelSelected={isModelSelected}
              isModelInOriginalSteps={isModelInOriginalSteps}
              onSelect={onSelect}
              disableScrollFading
            />
          )
        }
      </div>
      <div className="w-full flex justify-end my-2">
        <Button size="large" onClick={handleNext} disabled={!pipelineSteps?.length}>Next</Button>
      </div>
    </CardView>
  )
}

const ContextualAddStepFrame = ({
  handleNext, onClose, cardTitle, originalSteps 
}) => {
  const { addStep, removeStep, pipelineSteps } = usePipelineCreationContext();
  const { models, loading, error } = useModelSearch();

  const groupedModels = models?.length ? groupModelsById(models) : null;

  return (
    <PipelineAddStepFrame
      models={groupedModels}
      loading={loading}
      error={error}
      pipelineSteps={pipelineSteps}
      originalSteps={originalSteps}
      addStep={addStep}
      removeStep={removeStep}
      handleNext={handleNext}
      onClose={onClose}
      cardTitle={cardTitle}
    />
  )
}

export default ContextualAddStepFrame;
